import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { AnalyticsEventType } from '../../enums/analytics.enum';
import { IInteractionHeaderParams, ISendInteractionAboutGeppParams, ISendNavbarInteractionParams, ISendSelectNotificationParams } from '../../models/Analytics';
import { NativeStorageNamesV2, PAGE_VIEW_URL_MAP, SECTIONS } from '../../models/Constants';
import { MonitoringService } from '../../services/monitoring/monitoring.service';
import { NavigationService } from '../../services/navigation/navigation.service';
import { StorageData } from '../../services/utilities/storage-data.class';
import { MainAnalyticsService } from '../main-analytics/main-analytics.service';
import { UserAnalyticsService } from '../user-analytics/user-analytics.service';


export interface IPageViewParams {
  page_title: string;
  flow?: string;
  [key: string]: any;
}

export interface IPageViewEvent extends IPageViewParams {
  environment: string;
  origin: string;
  page_location: string;
  page_referrer: string;
}

type SECTIONS_VALUES = (typeof SECTIONS)[keyof typeof SECTIONS];

@Injectable({
  providedIn: 'root'
})
export class SharedAnalyticsService {

  constructor(
    private mainAnalyticsService: MainAnalyticsService,
    private navigationService: NavigationService,
    private userAnalyticsS: UserAnalyticsService,
    private monitoringS: MonitoringService
  ) { }

  /**
   * Dispara evento page_view
   */
  public async trackPageView(pageViewParams: IPageViewParams, section: SECTIONS_VALUES): Promise<void> {
    try {
      const pageViewConfig: IPageViewParams = {
        section,
        ...pageViewParams
      };
      // Construir evento GTM
      const eventComplete = await this.buildEvent(pageViewConfig);
      await Promise.all([
        this.mainAnalyticsService.sendEventGTM({ event: AnalyticsEventType.PAGE_VIEW, ...eventComplete }),
        this.mainAnalyticsService.logEventFirebase('screen_view', eventComplete)
      ]);
    } catch (err) {
      this.monitoringS.captureException(err);
    }

  }

  /**
   * Dispara evento page_view para modales fullscreen
   */
  public async trackPageViewModal(pageViewParams: IPageViewParams, section: SECTIONS_VALUES): Promise<void> {
    try {
      const pageViewConfig: IPageViewParams = {
        section,
        ...pageViewParams
      };

      const eventComplete = await this.buildEvent(pageViewConfig);
      await Promise.all([
        this.mainAnalyticsService.sendEventGTM({ event: AnalyticsEventType.PAGE_VIEW, ...eventComplete }),
        this.mainAnalyticsService.logEventFirebase('screen_view', eventComplete)
      ]);

    } catch (err) {
      this.monitoringS.captureException(err);
    }

  }

  /**
   * Dispara evento page_view para cambios de tab
   */
  public async trackPageViewTab(pageViewParams: IPageViewParams, section: SECTIONS_VALUES): Promise<void> {
    try {
      const pageViewConfig: IPageViewParams = {
        section,
        ...pageViewParams
      };

      const eventComplete = await this.buildEvent(pageViewConfig);

      await Promise.all([
        this.mainAnalyticsService.sendEventGTM({ event: AnalyticsEventType.PAGE_VIEW, ...eventComplete }),
        this.mainAnalyticsService.logEventFirebase('screen_view', eventComplete)
      ]);
    } catch (err) {
      this.monitoringS.captureException(err);
    }


  }

  /**
  * Envía el evento 'interaction_navbar' al hacer click en un item del bottom nav bar.
  */
  public async sendNavbarInteraction(params: ISendNavbarInteractionParams): Promise<void> {
    try {
      const { section, label } = params;
      const userProps = await this.userAnalyticsS.getUserProfileProperties();
      const platformOrigin = await StorageData.getDataStorage(NativeStorageNamesV2.PlatformOrigin);
      const eventData = {
        event: AnalyticsEventType.INTERACTION_NAVBAR,
        origin: platformOrigin || 'web',
        environment: (environment.enviromentAnalytics || 'dev').toLowerCase(),
        section,
        label
      };

      await Promise.all([
        this.mainAnalyticsService.sendEventGTM({ ...eventData, ...userProps }),
        this.mainAnalyticsService.logEventFirebase(eventData.event, eventData)
      ]);
    } catch (err) {
      this.monitoringS.captureException(err);
    }
  }

  /**
   * Envía el evento 'interaction_gepp' al hacer click en un item del bottom nav bar.
   */
  public async sendInteractionAboutGepp(params: ISendInteractionAboutGeppParams): Promise<void> {
    try {
      const { element, card_position, card_id } = params;
      const userProps = await this.userAnalyticsS.getUserProfileProperties();
      const platformOrigin = await StorageData.getDataStorage(NativeStorageNamesV2.PlatformOrigin);

      const eventData = {
        event: AnalyticsEventType.INTERACTION_ABOUT_GEPP,
        flow: 'consumidor',
        origin: platformOrigin || 'web',
        environment: (environment.enviromentAnalytics || 'dev').toLowerCase(),
        element,
        card_position,
        card_id
      };

      await Promise.all([
        this.mainAnalyticsService.sendEventGTM({ ...eventData, ...userProps }),
        this.mainAnalyticsService.logEventFirebase(eventData.event, eventData)
      ]);
    } catch (err) {
      this.monitoringS.captureException(err);
    }
  }

  /**
   * Envía el evento 'select_notification' al hacer click en un item del bottom nav bar.
   */
  public async sendSelectNotification(params: ISendSelectNotificationParams): Promise<void> {
    try {
      const { title } = params;
      const userProps = await this.userAnalyticsS.getUserProfileProperties();
      const platformOrigin = await StorageData.getDataStorage(NativeStorageNamesV2.PlatformOrigin);

      const eventData = {
        event: AnalyticsEventType.SELECT_NOTIFICATION,
        origin: platformOrigin || 'web',
        environment: (environment.enviromentAnalytics || 'dev').toLowerCase(),
        title
      };

      await Promise.all([
        this.mainAnalyticsService.sendEventGTM({ ...eventData, ...userProps }),
        this.mainAnalyticsService.logEventFirebase(eventData.event, eventData)
      ]);
    } catch (err) {
      this.monitoringS.captureException(err);
    }
  }

  /**
   * Envía el evento 'interaction_header' al interactuar con elementos del header.
   */
  public async trackInteractionHeader(params: IInteractionHeaderParams): Promise<void> {
    try {
      const { section, label } = params;
      const userProps = await this.userAnalyticsS.getUserProfileProperties();
      const platformOrigin = await StorageData.getDataStorage(NativeStorageNamesV2.PlatformOrigin);

      const eventData = {
        event: AnalyticsEventType.INTERACTION_HEADER,
        origin: platformOrigin || 'web',
        environment: (environment.enviromentAnalytics || 'dev').toLowerCase(),
        section,
        label
      };

      await Promise.all([
        this.mainAnalyticsService.sendEventGTM({ ...eventData, ...userProps }),
        this.mainAnalyticsService.logEventFirebase(eventData.event, eventData)
      ]);
    } catch (err) {
      this.monitoringS.captureException(err);
    }
  }

  /**
   * Envía el evento 'interaction_article' al abrir el detalle de una noticia.
   */
  public async sendInteractionArticle(params: { titulo_del_articulo: string; fecha_de_publicacion: string; categoria_del_articulo: string }): Promise<void> {
    try {
      const { titulo_del_articulo, fecha_de_publicacion, categoria_del_articulo } = params;
      const userProps = await this.userAnalyticsS.getUserProfileProperties();
      const platformOrigin = await StorageData.getDataStorage(NativeStorageNamesV2.PlatformOrigin);

      const eventData = {
        event: AnalyticsEventType.INTERACTION_ARTICLE,
        origin: platformOrigin || 'web',
        environment: (environment.enviromentAnalytics || 'dev').toLowerCase(),
        section: SECTIONS.PARA_TI,
        titulo_del_articulo,
        fecha_de_publicacion,
        categoria_del_articulo
      };

      await Promise.all([
        this.mainAnalyticsService.sendEventGTM({ ...eventData, ...userProps }),
        this.mainAnalyticsService.logEventFirebase(eventData.event, eventData)
      ]);
    } catch (err) {
      this.monitoringS.captureException(err);
    }
  }

  private async buildEvent(config: IPageViewParams): Promise<IPageViewEvent> {
    const platformOrigin = await StorageData.getDataStorage(NativeStorageNamesV2.PlatformOrigin);
    return {
      ...config,
      environment: environment.enviromentAnalytics.toLocaleLowerCase(),
      origin: platformOrigin,
      page_location: this.navigationService.getCurrentUrl() || '',
      page_referrer: this.navigationService.getPreviousUrl() || ''
    };
  }



  /**
   * Obtiene la configuración de page_view para una URL específica
   * @param url URL actual de la aplicación
   * @returns Configuración de page_view o null si no existe mapeo
   */
  public getPageViewConfig(url: string): any | null {
    try {
      const baseUrl = url.split('?')[0];
      return PAGE_VIEW_URL_MAP[baseUrl] || null;
    } catch (error) {
      return null;
    }
  }


}
